import { useRef, useState } from "react";
import { useFileTransfer } from "../hooks/useFileTransfer";

interface FileTransferPanelProps {
  sessionId: string;
  sessionToken: string;
  onClose: () => void;
}

function toMB(bytes: number): string {
  return (bytes / 1024 / 1024).toFixed(1);
}

export default function FileTransferPanel({
  sessionId,
  sessionToken,
  onClose,
}: FileTransferPanelProps) {
  const {
    files,
    total,
    quota,
    uploading,
    uploadProgress,
    error,
    toast,
    upload,
    download,
    remove,
  } = useFileTransfer(sessionId, sessionToken);

  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(true);
  };
  const handleDragLeave = () => setDragging(false);
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) void upload(f);
  };
  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) void upload(f);
    e.target.value = "";
  };

  return (
    <div
      style={{
        width: 280,
        height: "100%",
        background: "#111827",
        borderLeft: "1px solid #1f2937",
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "8px 12px",
          borderBottom: "1px solid #1f2937",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 600, color: "#e5e7eb" }}>
          Files {total > 0 && <span style={{ color: "#6b7280" }}>({total})</span>}
        </span>
        <button
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            color: "#6b7280",
            cursor: "pointer",
            fontSize: 14,
            padding: "2px 4px",
          }}
        >
          ✕
        </button>
      </div>

      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !uploading && fileInputRef.current?.click()}
        style={{
          margin: 8,
          padding: "14px 8px",
          border: `2px dashed ${dragging ? "#3b82f6" : "#374151"}`,
          borderRadius: 6,
          textAlign: "center",
          cursor: uploading ? "not-allowed" : "pointer",
          color: dragging ? "#60a5fa" : "#6b7280",
          fontSize: 12,
          transition: "border-color 0.15s, color 0.15s",
          flexShrink: 0,
        }}
      >
        {uploading ? `Uploading… ${uploadProgress}%` : "Drop file here or click"}
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: "none" }}
          onChange={handleFileInput}
        />
      </div>

      {uploading && (
        <div
          style={{
            margin: "0 8px 6px",
            height: 4,
            background: "#1f2937",
            borderRadius: 2,
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: `${uploadProgress}%`,
              height: "100%",
              background: "#3b82f6",
              borderRadius: 2,
              transition: "width 0.1s",
            }}
          />
        </div>
      )}

      {quota && quota.enabled !== false && (
        <div
          style={{
            padding: "3px 12px",
            fontSize: 11,
            color: "#6b7280",
            borderBottom: "1px solid #1f2937",
            flexShrink: 0,
          }}
        >
          Session: {toMB(quota.session.used_bytes)}/{toMB(quota.session.limit_bytes ?? 0)} MB
          {" · "}
          Daily: {toMB(quota.daily_user.used_bytes)}/{toMB(quota.daily_user.limit_bytes ?? 0)} MB
        </div>
      )}

      {error && (
        <div
          style={{
            margin: "4px 8px",
            padding: "4px 8px",
            background: "#7f1d1d",
            color: "#fca5a5",
            borderRadius: 4,
            fontSize: 11,
            flexShrink: 0,
          }}
        >
          {error}
        </div>
      )}

      {toast && (
        <div
          style={{
            margin: "4px 8px",
            padding: "4px 8px",
            background: toast.type === "ok" ? "#064e3b" : "#7f1d1d",
            color: toast.type === "ok" ? "#6ee7b7" : "#fca5a5",
            borderRadius: 4,
            fontSize: 11,
            flexShrink: 0,
          }}
        >
          {toast.msg}
        </div>
      )}

      <div style={{ flex: 1, overflowY: "auto" }}>
        {files.length === 0 ? (
          <div
            style={{
              padding: 16,
              textAlign: "center",
              color: "#4b5563",
              fontSize: 12,
            }}
          >
            No files
          </div>
        ) : (
          files.map((f) => (
            <div
              key={f.path}
              style={{
                padding: "6px 10px",
                borderBottom: "1px solid #1f2937",
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 12,
              }}
            >
              <span
                style={{
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  color: "#d1d5db",
                }}
                title={f.path}
              >
                {f.name}
              </span>
              <span style={{ color: "#6b7280", fontSize: 10, flexShrink: 0 }}>
                {toMB(f.size)}MB
              </span>
              <button
                onClick={() => void download(f.path)}
                title="Download"
                style={{
                  background: "#1f2937",
                  border: "none",
                  borderRadius: 3,
                  color: "#9ca3af",
                  cursor: "pointer",
                  padding: "2px 6px",
                  fontSize: 11,
                  flexShrink: 0,
                }}
              >
                ↓
              </button>
              <button
                onClick={() => void remove(f.path)}
                title="Delete"
                style={{
                  background: "#1f2937",
                  border: "none",
                  borderRadius: 3,
                  color: "#9ca3af",
                  cursor: "pointer",
                  padding: "2px 6px",
                  fontSize: 11,
                  flexShrink: 0,
                }}
              >
                ✕
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
